import React, { useContext, useEffect, useState } from 'react';
import { clsx } from 'clsx';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';

import Layout from '../../common_components/Layout';
import { AppContext } from '../contexts';
import InnerModal from '../components/InnerModal';
import { getNextStep } from '../utils';
import Loading from '../../common_components/Loading';

export default function ColumnValidation({ data, name }) {
  const app = useContext(AppContext);
  const stepsApi = app.APIs.steps;

  const [editedVariable, setEditedVariable] = useState();
  const [isLoading, setIsLoading] = useState(true);

  const { missingRequired, missingUserDefined, missingOptional } =
    getVariablesSummary(data);
  const status =
    missingRequired.length > 0
      ? 'error'
      : missingOptional.length > 0 || missingUserDefined.length > 0
        ? 'warning'
        : 'ok';

  const handleClickVariable = (variable) => {
    if (variable.kind == 'user-defined-new') {
      setEditedVariable(variable);
    }
  };
  const handleLoadFileAgainBtn = () => {
    app.setVisibleStep('dataimport');
  };
  const handleImportDataBtn = () => {
    stepsApi
      .validateColumnsConfirm(app.activeProject.id)
      .then((projectData) => {
        app.setActiveProject(projectData);
        app.setVisibleStep(getNextStep(name));
      });
  };

  useEffect(() => {
    async function updateVariables() {
      await stepsApi
        .validateColumnsInit(app.activeProject.id)
        .then((projectData) => {
          app.setActiveProject(projectData);
          setIsLoading(false);
        });
    }
    if (!data || data.length == 0) {
      updateVariables();
    } else {
      setIsLoading(false);
    }
  }, []);

  return (
    <Layout
      sideContent={
        <div className="flex flex-col h-full">
          <h2 className="text-white mt-10">Dostępne zmienne:</h2>
          <ul className="list-none p-4 bg-slate-500 basis-2/3 rounded-lg">
            {data?.map((var_, i) => (
              <li className="m-0 p-0" key={var_.name + i}>
                {var_.description || var_.kind == 'user-defined-confirmed' ? (
                  <Tippy
                    content={
                      !['user-defined-confirmed', 'user-defined-new'].includes(
                        var_.kind,
                      )
                        ? var_.description
                        : !var_.missing
                          ? `Zmienna ${var_.name_full} (${var_.name}) - Jednostka: ${var_.unit} Typ: ${var_.type}`
                          : ''
                    }
                    placement="top"
                    theme="custom-dark"
                  >
                    <button
                      className={clsx({
                        'text-red-300': var_.kind == 'required' && var_.missing,
                        'text-warning':
                          (var_.kind == 'optional' && var_.missing) ||
                          var_.kind == 'user-defined-new',
                      })}
                      onClick={() => handleClickVariable(var_)}
                    >
                      {var_.missing}
                      {var_.name}
                      {var_.kind == 'required' ? ' *' : ''}
                    </button>
                  </Tippy>
                ) : (
                  <button
                    className={clsx({
                      'text-red-300': var_.kind == 'required' && var_.missing,
                      'text-warning':
                        (var_.kind == 'optional' && var_.missing) ||
                        var_.kind == 'user-defined-new',
                    })}
                    onClick={() => handleClickVariable(var_)}
                  >
                    {var_.missing}
                    {var_.name}
                    {var_.kind == 'required' ? ' *' : ''}
                  </button>
                )}
              </li>
            ))}
          </ul>
          <button className="btn mt-auto" onClick={handleLoadFileAgainBtn}>
            Wczytaj ponownie plik
          </button>
        </div>
      }
      title="Walidacja nagłówkow danych"
      mainContent={
        <>
          <div className="flex flex-col grow-1 justify-center items-center h-full">
            {isLoading ? (
              <Loading />
            ) : status == 'error' || status == 'warning' ? (
              <>
                {status == 'error' && (
                  <>
                    <h2 className="text-error">Błędy:</h2>
                    <ol>
                      {missingRequired.length > 0 && (
                        <li>
                          W importowanym pliku brakuje zmiennych:{' '}
                          {missingRequired.join(', ')}. Uzupełnij brakujące
                          zmienne w pliku danych i wczytaj ponownie zestaw
                          danych.
                        </li>
                      )}
                    </ol>
                  </>
                )}
                {(missingOptional.length > 0 ||
                  missingUserDefined.length > 0) && (
                  <>
                    <h2 className="text-warning">Ostrzeżenia:</h2>
                    <ol>
                      {missingOptional.length > 0 && (
                        <li>
                          W importowanym pliku brakuje następujących zmiennych
                          zalecanych: {missingOptional.join(', ')} – zalecamy
                          uzupełnienie danych. Nie jest ono wymagane.
                        </li>
                      )}
                      {missingUserDefined.length > 0 && (
                        <li>
                          Wykryto niestandardowe zmienne:{' '}
                          {missingUserDefined.join(', ')}. Uzupełnij informacje
                          o zmiennych. W tym celu kliknij na podświetlone pola:{' '}
                          {missingUserDefined.join(', ')} w lewym panelu.
                        </li>
                      )}
                    </ol>
                  </>
                )}
              </>
            ) : (
              <h2 className="text-center text-success">
                Wczytane dane zawierają wszystkie obligatoryjne i zalecane
                zmienne.
              </h2>
            )}
            {!isLoading &&
              (status == 'error' ? (
                <Tippy
                  content="Popraw nagłówki i wczytaj ponownie plik aby przejść dalej"
                  placement="top"
                  theme="custom-dark"
                >
                  <div>
                    <button
                      className={clsx(
                        'btn self-center mt-8 !pointer-events-auto',
                        status == 'warning' && 'btn-warning',
                        status == 'ok' && 'btn-success',
                      )}
                      onClick={handleImportDataBtn}
                      disabled={status == 'error'}
                    >
                      Zaimportuj dane {status == 'warning' && 'pomimo to'}
                    </button>
                  </div>
                </Tippy>
              ) : (
                <button
                  className={clsx(
                    'btn self-center mt-8 !pointer-events-auto',
                    status == 'warning' && 'btn-warning',
                    status == 'ok' && 'btn-success',
                  )}
                  onClick={handleImportDataBtn}
                  disabled={status == 'error'}
                >
                  Zaimportuj dane {status == 'warning' && 'pomimo to'}
                </button>
              ))}
          </div>
          {editedVariable && (
            <InnerModal>
              <AddVariableForm
                editedVariable={editedVariable}
                setEditedVariable={setEditedVariable}
                thisStepName={name}
                thisStepData={data}
              />
            </InnerModal>
          )}
        </>
      }
    ></Layout>
  );
}

function AddVariableForm({ editedVariable, setEditedVariable }) {
  const app = useContext(AppContext);
  const stepsApi = app.APIs.steps;

  const [error, setError] = useState(false);

  const handleSubmit = (event) => {
    event.preventDefault();
    const { name, name_full, unit, type } = event.target.elements;
    const additionalColumn = {
      name: name.value,
      kind: 'user-defined-confirmed',
      name_full: name_full.value,
      unit: unit.value,
      type: type.value,
    };
    stepsApi
      .validateAdditionalColumn(app.activeProject.id, additionalColumn)
      .then((response) => {
        if (response.status == 'wrong-name') {
          setError(true);
        } else {
          app.setActiveProject(response);
          setEditedVariable(null);
        }
      });
  };
  const handleCancelEditing = () => {
    setEditedVariable(null);
  };

  return (
    <form className="flex flex-col gap-2" onSubmit={handleSubmit}>
      <h3 className="mt-0">Dodaj nową zmienną</h3>
      <input
        type="text"
        name="name"
        placeholder="nazwa skrócona (zmienna)"
        required
        className="input input-sm input-bordered w-full max-w-xs"
        defaultValue={editedVariable.name}
        disabled // we don't want to allow user to change the name
        // that would require checking for name duplicates on backend
      />
      <input
        type="text"
        name="name_full"
        placeholder="nazwa pełna (nazwa)"
        required
        className="input input-sm input-bordered w-full max-w-xs"
      />
      <input
        type="text"
        name="unit"
        required
        placeholder="jednostka"
        className="input input-sm input-bordered w-full max-w-xs"
      />
      <select
        required
        name="type"
        className="select select-sm  select-bordered w-full max-w-xs"
        defaultValue={''}
        aria-label="Wybierz typ zmiennej"
      >
        <option value="" disabled hidden>
          typ zmiennej
        </option>
        <option value={'date'}>data</option>
        <option value={'quality'}>jakościowa</option>
        <option value={'numeric'}>ilościowa</option>
      </select>
      {error && <p className="text-error">Kolumna z tą nazwą już istnieje</p>}
      <div className="w-full flex justify-between mt-4">
        <button
          className="btn btn-sm btn-secondary"
          onClick={handleCancelEditing}
        >
          Anuluj
        </button>
        <button type="submit" name="" className="btn btn-sm btn-success">
          Utwórz
        </button>
      </div>
    </form>
  );
}

const getVariablesSummary = (variables) => {
  const missingRequired = [];
  const missingUserDefined = [];
  const missingOptional = [];

  variables?.forEach(({ name, kind, missing, validated }) => {
    if (kind == 'required' && missing) {
      missingRequired.push(name);
    } else if (kind == 'user-defined-new' && !validated) {
      missingUserDefined.push(name);
    } else if (kind == 'optional' && missing) {
      missingOptional.push(name);
    }
  });

  return {
    missingRequired,
    missingUserDefined,
    missingOptional,
  };
};
